import { SliceCaseReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IBestOf2025Category } from 'app/components/BestOf2025/hooks/useBestOf2025';
import {
  IBestOf2025VotingCategory,
  IBestOf2025VotingResource,
} from 'app/components/BestOf2025/hooks/useBestOf2025Voting';
import { BestOf2025Phase, BestOf2025Phases } from 'app/components/BestOf2025/interfaces';

export interface IBestOfState {
  year: number;
  phase: BestOf2025Phase;
  categories: IBestOf2025Category[];
  currentCategory: IBestOf2025VotingCategory | null;
  voting: boolean;
  votingPercentage: number | null;
  resourcesByCategory: { [key: string]: IBestOf2025VotingResource[] };
  votesAvailablePerCategory: number;
  myVotesByCategory: { [key: string]: string[] };
  myVotes: string[];
  myResources: IBestOf2025VotingResource[];
  // Phase Two specific state
  phaseTwoCompletedCategories: { [categoryName: string]: boolean };
  phaseTwoSelectedByCategory: { [categoryName: string]: string[] };
}

export const slice = createSlice<IBestOfState, SliceCaseReducers<IBestOfState>>({
  name: 'best-of',
  initialState: {
    year: 2025,
    phase: null,
    categories: [],
    currentCategory: null,
    voting: false,
    votingPercentage: null,
    resourcesByCategory: {},
    votesAvailablePerCategory: 0,
    myVotesByCategory: {},
    myVotes: [],
    myResources: [],
    phaseTwoCompletedCategories: {},
    phaseTwoSelectedByCategory: {},
  },
  reducers: {
    setPhase: (state, action: { payload: BestOf2025Phase }) => ({
      ...state,
      phase: action.payload,
    }),
    setYear: (state, action: { payload: number }) => ({
      ...state,
      year: action.payload,
    }),
    setCategories: (state, action: { payload: IBestOf2025Category[] }) => ({
      ...state,
      categories: action.payload,
    }),
    setCurrentCategory: (state, action: { payload: IBestOf2025VotingCategory | null }) => ({
      ...state,
      currentCategory: action.payload,
    }),
    setVoting: (state, action: { payload: boolean }) => ({
      ...state,
      voting: action.payload,
    }),
    setVotingPercentage: (state, action: { payload: number }) => ({
      ...state,
      votingPercentage: action.payload,
    }),
    setVotesAvailablePerCategory: (state, action: { payload: number }) => ({
      ...state,
      votesAvailablePerCategory: action.payload,
    }),
    setMyVotesByCategory: (state, action: { payload: { [key: string]: string[] } }) => ({
      ...state,
      myVotesByCategory: action.payload,
    }),
    setMyVotes: (state, action: { payload: string[] }) => ({
      ...state,
      myVotes: action.payload,
    }),
    pushMyVote: (state, action: { payload: string }) => ({
      ...state,
      myVotes: [...state.myVotes, action.payload],
    }),
    removeMyVote: (state, action: { payload: string }) => ({
      ...state,
      myVotes: state.myVotes.filter((vote) => vote !== action.payload),
    }),
    setResourcesByCategory: (
      state,
      action: { payload: { [key: string]: IBestOf2025VotingResource[] } }
    ) => ({
      ...state,
      resourcesByCategory: action.payload,
    }),
    setMyResources: (state, action: { payload: IBestOf2025VotingResource[] }) => ({
      ...state,
      myResources: action.payload,
    }),
    pushMyResource: (state, action: { payload: IBestOf2025VotingResource }) => ({
      ...state,
      myResources: [...state.myResources, action.payload],
    }),
    removeMyResource: (state, action: { payload: string }) => ({
      ...state,
      myResources: state.myResources.filter((resource) => resource.uuid !== action.payload),
    }),
    // Phase Two actions
    setPhaseTwoCompletedCategory: (
      state,
      action: { payload: { categoryName: string; completed: boolean } }
    ) => ({
      ...state,
      phaseTwoCompletedCategories: {
        ...state.phaseTwoCompletedCategories,
        [action.payload.categoryName]: action.payload.completed,
      },
    }),
    setPhaseTwoSelectedForCategory: (
      state,
      action: { payload: { categoryName: string; resourceIds: string[] } }
    ) => ({
      ...state,
      phaseTwoSelectedByCategory: {
        ...state.phaseTwoSelectedByCategory,
        [action.payload.categoryName]: action.payload.resourceIds,
      },
    }),
  },
});

export const {
  setPhase,
  setYear,
  setCategories,
  setCurrentCategory,
  setVoting,
  setVotingPercentage,
  setVotesAvailablePerCategory,
  setMyVotes,
  setMyVotesByCategory,
  pushMyVote,
  removeMyVote,
  setResourcesByCategory,
  setMyResources,
  pushMyResource,
  removeMyResource,
  setPhaseTwoCompletedCategory,
  setPhaseTwoSelectedForCategory,
} = slice.actions;

export const bestOfYearSelector = (state): number => state.bestOf.year;
export const bestOfPhaseSelector = (state): BestOf2025Phase | null => state.bestOf.phase;
export const bestOfCategoriesSelector = (state): IBestOf2025Category[] => state.bestOf.categories;
export const bestOfVotingSelector = (state): boolean => state.bestOf.voting;
export const bestOfVotingPercentageSelector = (state): number | null =>
  state.bestOf.votingPercentage;
export const bestOfMyVotesSelector = (state): string[] => state.bestOf.myVotes;
export const bestOfMyVotesByCategorySelector = (state): { [key: string]: string[] } =>
  state.bestOf.myVotesByCategory;
export const bestOfResourcesByCategorySelector = (
  state
): { [key: string]: IBestOf2025VotingResource[] } => state.bestOf.resourcesByCategory;
export const bestOfVotesAvailablePerCategorySelector = (state): number =>
  state.bestOf.votesAvailablePerCategory;
export const bestOfMyResourcesSelector = (state): IBestOf2025VotingResource[] =>
  state.bestOf.myResources;
export const bestOfCurrentCategorySelector = (state): IBestOf2025VotingCategory | null =>
  state.bestOf.currentCategory;
export const bestOfPhaseTwoCompletedCategoriesSelector = (
  state
): { [categoryName: string]: boolean } => state.bestOf.phaseTwoCompletedCategories;
export const bestOfPhaseTwoSelectedByCategorySelector = (
  state
): { [categoryName: string]: string[] } => state.bestOf.phaseTwoSelectedByCategory;

export default slice.reducer;
